import { ChronologicalData } from './utils/chronological-data.interface';
import { GroupBy } from './utils/group-by.enum';
import { GroupedChronologicalData } from './utils/grouped-chronological-data.class';
import {
  GroupByStrategyBase,
  GroupByDayStrategy,
} from './strategies/group-strategy-base.class';
import { GroupByWeekStrategy } from './strategies/group-by-week-strategy.class';
import { GroupByMonthStrategy } from './strategies/group-by-month-strategy.class';
import { GroupByYearStrategy } from './strategies/group-by-year-strategy.class';
import { GroupByQuarterStrategy } from './strategies/group-by-quarter-strategy.class';

/* It takes a list of chronological data and groups it by a given time period */
export class ChronologicalGrouper<T> {
  private locale: string | undefined;

  constructor(locale?: string) {
    this.locale = locale;
  }

  /**
   * "Given a list of data, group it by the given strategy."
   *
   * The first thing we do is get the strategy that we want to use. We do this by
   * calling the `getStrategy` function
   * @param {ChronologicalData<T>[]} data - ChronologicalData<T>[]
   * @param {GroupBy} groupBy - GroupBy
   * @returns An array of objects with the following structure:
   * ```
   * {
   *   date: Date,
   *   data: T[]
   * }
   * ```
   */
  group(
    data: ChronologicalData<T>[],
    groupBy: GroupBy
  ): GroupedChronologicalData<T> {
    const strategy: GroupByStrategyBase<T> = this.getStrategy(groupBy);
    return strategy.group(data, groupBy, this.locale);
  }

  /**
   * It returns a strategy object based on the groupBy parameter
   * @param {GroupBy} groupBy - GroupBy - this is the enum that we created earlier.
   * @returns A new instance of the strategy class that is being passed in.
   */
  private getStrategy(groupBy: GroupBy) {
    let strategy: GroupByStrategyBase<T>;

    switch (groupBy) {
      case GroupBy.DAY:
        strategy = new GroupByDayStrategy<T>();
        break;
      case GroupBy.WEEK:
        strategy = new GroupByWeekStrategy<T>();
        break;
      case GroupBy.MONTH:
        strategy = new GroupByMonthStrategy<T>();
        break;
      case GroupBy.YEAR:
        strategy = new GroupByYearStrategy<T>();
        break;
      case GroupBy.QUARTER:
        strategy = new GroupByQuarterStrategy<T>();
        break;
      default:
        strategy = new GroupByDayStrategy<T>();
    }

    return strategy;
  }
}
